import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { Typography, useMediaQuery, theme } from "@l2r-front/l2r-ui"
import { formatMetersToKilometers } from "@l2r-front/l2r-utils"

import { appRoad } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import * as Styled from "./Dashboard.styled"

const GridAreas = [
    "1 / 1 / 3 / 1",
    "1 / 2 / 3 / 2",
    "3 / 1 / 3 / 3",
]

export const Dashboard = () => {
    const { selectedNetwork } = useNetworksStateContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"))

    const activeModules = useMemo(() => {
        return selectedNetwork ? Object.values(appRoad.modules).filter(module => selectedNetwork.modules.includes(module.id)) : []
    }, [selectedNetwork])

    const kpis = useMemo(() => {
        return Object.values(activeModules)
            .reduce((kpisArray, module) => {
                if (module.kpis) {
                    kpisArray.push(module.kpis)
                }

                return kpisArray.flat()
            }, [])
            .sort((a, b) => a.gridIndex - b.gridIndex)
    }, [activeModules])

    const sidebarKpis = useMemo(() => {
        return kpis
            .sort((a, b) => a.position - b.position)
            .filter(kpi => kpi.parent === "sidebar")
            .map((kpi, index) => {
                const Component = kpi.component
                return <Component key={`kpi-${index}`} style={{ height: "fit-content" }} />
            })
    }, [kpis])

    const listKpis = useMemo(() => {
        if (kpis) {
            if (!isLargeScreen) {
                const mobileDashboard = kpis
                    .sort((a, b) => a.mobileOrder - b.mobileOrder)
                    .map((kpi, index) => {
                        const Component = kpi.component
                        return <Component key={`kpi-${index}`} style={{
                            minHeight: kpi.mobileHeight,
                        }} />
                    })
                return <Styled.Scrollable id="kpi-list">
                    {mobileDashboard}
                </Styled.Scrollable>
            }
            return kpis
                .filter(kpi => kpi.parent === "list")
                .map((kpi, index) => {
                    const Component = kpi.component
                    return <Component key={`kpi-${index}`} style={{
                        gridArea: kpi.gridIndex < 2 ? GridAreas[index] : GridAreas[kpi.gridIndex],
                        height: kpi.height,
                    }} />
                })
        }
    }, [kpis, isLargeScreen])

    const desktopLayout = useMemo(() => {
        return <>
            <Styled.Sidebar>
                <Styled.NetworkName id="network-name" variant="H1">
                    {selectedNetwork ? selectedNetwork.nameLong : ""}
                </Styled.NetworkName>
                <Styled.Wrapper>
                    <Styled.NetworkLength>
                        <Typography variant="H3">
                            {`${t(I18N_NAMESPACE, "components.networkLength.label")}`}
                        </Typography>
                        <Typography variant="H1">
                            {`${formatMetersToKilometers(selectedNetwork.length)}`}
                        </Typography>
                    </Styled.NetworkLength>
                </Styled.Wrapper>
                <Styled.PanelWrapper>
                    {sidebarKpis}
                </Styled.PanelWrapper>
            </Styled.Sidebar>
            <Styled.Container>
                <Styled.GridContainer>
                    {kpis.length ? listKpis : (
                        <Typography id="noActiveApplications-text" variant="Small">
                            {t(I18N_NAMESPACE, "pages.messages.noActiveApplications")}
                        </Typography>
                    )}
                </Styled.GridContainer>
            </Styled.Container>
        </>
    }, [sidebarKpis, listKpis, kpis, selectedNetwork, t])

    const mobileLayout = useMemo(() => {
        return <Styled.FlexContainer>
            <Styled.NetworkName id="network-name" variant="H1">
                {selectedNetwork ? selectedNetwork.name : ""}
            </Styled.NetworkName>
            <Styled.Wrapper>
                <Styled.NetworkLength>
                    <Typography variant="H3">
                        {`${t(I18N_NAMESPACE, "components.networkLength.label")}`}
                    </Typography>
                    <Typography variant="H1">
                        {`${formatMetersToKilometers(selectedNetwork.length)}`}
                    </Typography>
                </Styled.NetworkLength>
            </Styled.Wrapper>
            {kpis.length ? listKpis : (
                <Typography id="noActiveApplications-text" variant="Small">
                    {t(I18N_NAMESPACE, "pages.messages.noActiveApplications")}
                </Typography>
            )}
        </Styled.FlexContainer>
    }, [listKpis, kpis, selectedNetwork, t])

    return (
        <Styled.PanelContent>
            {isLargeScreen ? desktopLayout : mobileLayout}
        </Styled.PanelContent>
    )
}